// src/components/Departments/DepartmentDetails.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Pagination, // ⬅️ added
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import FolderIcon from "@mui/icons-material/Folder";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SortIcon from "@mui/icons-material/Sort";

import { baseUrl } from "../../constants";
import { handleDateWithTime } from "../../utils/date-handlers";

type MemberItem = {
  user: {
    id: number;
    username: string;
    profile?: { imageUrl?: string | null; name?: string | null } | null;
  };
  position: { id: number; name: string };
};

type ProjectWithStats = {
  id: number;
  name: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  tags?: string[] | null;
  archived?: boolean;
  defaultVisibility?: string;
  startDate?: string | null;
  deadline?: string | null;
  taskTotal: number;
  taskDone: number;
  taskOverdue: number;
  byStatus: Record<string, number>;
};

type DepartmentProjectsResponse = {
  department: { id: number; name: string };
  projects: ProjectWithStats[];
};

type SortKey =
  | "name"
  | "dueAsc"
  | "dueDesc"
  | "progressDesc"
  | "overdueDesc"
  | "tasksDesc";

const tokenHeader = () => {
  const t =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};

const DepartmentDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>();
  const did = Number(departmentId || 0);

  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);

  const [meta, setMeta] = useState<{ id: number; name: string } | null>(null);
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);

  const [qRaw, setQRaw] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("progressDesc");

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const abortRef = useRef<AbortController | null>(null);

  // ⬇️ Pagination (client-side)
  const PAGE_SIZE = 50;
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    const t = setTimeout(() => setQ(qRaw.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qRaw]);

  // Reset to page 1 on filter/sort change
  useEffect(() => {
    setPage(1);
  }, [q, sortKey]);

  const loadAll = useCallback(async () => {
    if (!did) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const { data } = await axios.get<DepartmentProjectsResponse>(
        `${baseUrl}/departments/${did}/projects`,
        { headers: tokenHeader(), signal: ctrl.signal }
      );
      setMeta(data.department);
      setProjects(data.projects || []);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to load department info",
        severity: "error",
      });
      setMeta(null);
      setProjects([]);
    } finally {
      setLoading(false);
    }

    setMembersLoading(true);
    try {
      const { data } = await axios.get<MemberItem[]>(
        `${baseUrl}/departments/${did}/members`,
        { headers: tokenHeader() }
      );
      setMembers(Array.isArray(data) ? data : []);
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [did]);

  useEffect(() => {
    loadAll();
    return () => abortRef.current?.abort();
  }, [loadAll]);

  const stats = useMemo(() => {
    const totalTasks = projects.reduce((s, p) => s + (p.taskTotal || 0), 0);
    const doneTasks = projects.reduce((s, p) => s + (p.taskDone || 0), 0);
    const overdueTasks = projects.reduce((s, p) => s + (p.taskOverdue || 0), 0);
    const progress = totalTasks
      ? Math.round((doneTasks / totalTasks) * 100)
      : 0;
    return { totalTasks, doneTasks, overdueTasks, progress };
  }, [projects]);

  const filteredSorted = useMemo(() => {
    const base = (projects || []).filter((p) => {
      if (!q) return true;
      const hay = `${p.name || ""} ${p.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
    const sorted = [...base].sort((a, b) => {
      switch (sortKey) {
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "dueAsc":
          return (
            new Date(a.deadline || 0).getTime() -
            new Date(b.deadline || 0).getTime()
          );
        case "dueDesc":
          return (
            new Date(b.deadline || 0).getTime() -
            new Date(a.deadline || 0).getTime()
          );
        case "tasksDesc":
          return (b.taskTotal || 0) - (a.taskTotal || 0);
        case "overdueDesc":
          return (b.taskOverdue || 0) - (a.taskOverdue || 0);
        case "progressDesc": {
          const pa = a.taskTotal ? a.taskDone / a.taskTotal : 0;
          const pb = b.taskTotal ? b.taskDone / b.taskTotal : 0;
          return pb - pa;
        }
        default:
          return 0;
      }
    });
    return sorted;
  }, [projects, q, sortKey]);

  // ⬇️ Paged slice (client-side)
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, filteredSorted.length);
  const pagedProjects = filteredSorted.slice(startIdx, endIdx);

  const exportProjectsExcel = async () => {
    try {
      if (!filteredSorted.length) {
        setSnackbar({
          open: true,
          message: "No projects to export.",
          severity: "info",
        });
        return;
      }

      const rows = filteredSorted.map((p) => ({
        id: p.id,
        name: p.name, // Arabic-safe
        status: p.status || "",
        priority: p.priority || "",
        startDate: p.startDate ? handleDateWithTime(p.startDate) : "",
        deadline: p.deadline ? handleDateWithTime(p.deadline) : "",
        taskTotal: p.taskTotal,
        taskDone: p.taskDone,
        taskOverdue: p.taskOverdue,
        progress: p.taskTotal
          ? `${Math.round((p.taskDone / p.taskTotal) * 100)}%`
          : "0%",
        tags: (p.tags || []).join(" | "),
      }));

      const mod: any = await import("xlsx");
      const XLSX = mod.default ?? mod;

      const headers = [
        "id",
        "name",
        "status",
        "priority",
        "startDate",
        "deadline",
        "taskTotal",
        "taskDone",
        "taskOverdue",
        "progress",
        "tags",
      ] as const;

      const ws = XLSX.utils.json_to_sheet(rows, { header: headers as any });

      // Autosize columns so Arabic (RTL) text is fully visible
      (ws as any)["!cols"] = headers.map((h) => {
        const maxLen = Math.max(
          String(h).length,
          ...rows.map((r) => String((r as any)[h] ?? "").length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");

      const safeName =
        (meta?.name?.replace(/[\\/:*?"<>|]/g, "_") || `department-${did}`) +
        "-projects.xlsx";

      XLSX.writeFile(wb, safeName);

      setSnackbar({
        open: true,
        message: "Exported to Excel.",
        severity: "success",
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Excel export failed.",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  if (loading) {
    return (
      <Box
        p={3}
        display="flex"
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!meta) {
    return (
      <Box p={3} pt="100px">
        <Typography variant="h6">
          Department not found or not accessible.
        </Typography>
        <Button
          sx={{ mt: 1 }}
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={1.25}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: "#5DB0FF" }} />
          </IconButton>
          <BusinessIcon color="action" />
          <Typography variant="h5" fontWeight={700}>
            {meta.name}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
          <Tooltip title="Export projects (Excel)">
            <span>
              <IconButton
                onClick={exportProjectsExcel}
                disabled={!filteredSorted.length}
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton onClick={loadAll}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Department Progress
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.doneTasks}/{stats.totalTasks} tasks done
                    {stats.overdueTasks
                      ? ` • ${stats.overdueTasks} overdue`
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 260 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.5}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.progress}
                    sx={{ height: 10, borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Members */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ py: 2, height: "100%" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Members
                </Typography>
                {membersLoading && <CircularProgress size={18} />}
              </Box>

              {!membersLoading && members.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No members found or no access.
                </Typography>
              ) : (
                <AvatarGroup
                  max={10}
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 28,
                      height: 28,
                      fontSize: 13,
                    },
                  }}
                >
                  {members.map((m) => {
                    const display = m.user.profile?.name || m.user.username;
                    return (
                      <Tooltip
                        key={m.user.id}
                        title={`${display}${
                          m.position?.name ? ` • ${m.position.name}` : ""
                        }`}
                      >
                        <Avatar src={m.user.profile?.imageUrl || undefined}>
                          {(display || "?").charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    );
                  })}
                </AvatarGroup>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search projects"
                placeholder="Name or description…"
                value={qRaw}
                onChange={(e) => setQRaw(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                display="flex"
                alignItems="center"
                gap={1.25}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <SortIcon fontSize="small" />
                <TextField
                  select
                  size="small"
                  label="Sort"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  sx={{ width: 240 }}
                >
                  <MenuItem value="progressDesc">Progress: High → Low</MenuItem>
                  <MenuItem value="overdueDesc">Overdue: High → Low</MenuItem>
                  <MenuItem value="tasksDesc">Tasks: High → Low</MenuItem>
                  <MenuItem value="dueAsc">Due date: Oldest first</MenuItem>
                  <MenuItem value="dueDesc">Due date: Newest first</MenuItem>
                  <MenuItem value="name">Name A → Z</MenuItem>
                </TextField>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Projects (with pagination) */}
      {!filteredSorted.length ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {projects.length
                ? "No projects match your filters."
                : "No projects in this department."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              gap={1}
              flexWrap="wrap"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Projects ({filteredSorted.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredSorted.length === 0 ? 0 : startIdx + 1}–
                {endIdx} of {filteredSorted.length}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />
            <List dense>
              {pagedProjects.map((p) => {
                const progress = p.taskTotal
                  ? Math.round((p.taskDone / p.taskTotal) * 100)
                  : 0;
                const overdue = p.taskOverdue > 0;
                return (
                  <ListItem
                    key={p.id}
                    onDoubleClick={() => navigate(`/projects/${p.id}`)}
                    sx={{
                      borderRadius: 1,
                      "&:hover": { bgcolor: theme.palette.action.hover },
                      cursor: "pointer",
                      borderLeft: overdue
                        ? `3px solid ${theme.palette.error.main}`
                        : "3px solid transparent",
                      pl: overdue ? 0.5 : 0,
                    }}
                    secondaryAction={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 140 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                        {!!p.taskOverdue && (
                          <Chip
                            size="small"
                            color="error"
                            label={`${p.taskOverdue} overdue`}
                          />
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                          onClick={() => navigate(`/projects/${p.id}`)}
                        >
                          <FolderIcon fontSize="small" />
                          <Typography variant="body1" fontWeight={600}>
                            {p.name}
                          </Typography>
                          {p.status && <Chip size="small" label={p.status} />}
                          {p.priority && (
                            <Chip size="small" label={p.priority} />
                          )}
                          {p.tags?.length ? (
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {p.tags.slice(0, 3).map((t, i) => (
                                <Chip
                                  key={`${p.id}-tag-${i}`}
                                  size="small"
                                  variant="outlined"
                                  label={t}
                                />
                              ))}
                              {p.tags.length > 3 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`+${p.tags.length - 3}`}
                                />
                              )}
                            </Box>
                          ) : null}
                          {p.archived && (
                            <Chip
                              size="small"
                              color="default"
                              label="Archived"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          flexWrap="wrap"
                          mt={0.5}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {p.startDate ? (
                              <>
                                <CalendarTodayIcon sx={{ fontSize: 14 }} />{" "}
                                Start: {handleDateWithTime(p.startDate)}
                              </>
                            ) : (
                              "Start: —"
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.deadline ? (
                              <>
                                <AccessTimeIcon sx={{ fontSize: 14 }} /> Due:{" "}
                                {handleDateWithTime(p.deadline)}
                              </>
                            ) : (
                              "Due: —"
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tasks: {p.taskDone}/{p.taskTotal}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {/* ⬇️ Pagination controls */}
            {totalPages > 1 && (
              <>
                <Divider sx={{ mt: 1, mb: 1 }} />
                <Box display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartmentDetails;
